import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { QueryClient } from 'react-query';

import type { ILegacyDashboardUrlProps } from '~/helpers/getLegacyDashboardUrl';
import type { IApiGetUserResponseBody } from '~/lib/api/modules/Users';
import type { IAppRegion, IAppRegionSettings } from '~/lib/appSettings';
import type { IStorage } from '~/lib/storage';
import type { IJsonRequest, IResponse } from './fetchJson';
import { getLegacyDashboardUrl } from '~/helpers/getLegacyDashboardUrl';
import { appRegionsSettings, getAppSettings } from '~/lib/appSettings';
import { storageFactory } from '~/lib/storage';
import { ApiError } from './ApiError';
import { fetchJson } from './fetchJson';
import { AdsApi } from './modules/Ads';
import { AppAdminsApi } from './modules/AppAdmins';
import { AppResourcesApi } from './modules/AppResources';
import { AppsApi } from './modules/Apps';
import { AppSettingsApi } from './modules/AppSettings';
import { ArticlesApi } from './modules/Articles';
import { AuthApi } from './modules/Auth';
import { BuildsApi } from './modules/Builds';
import { CategoriesApi } from './modules/Categories';
import { CrowdApi } from './modules/Crowd';
import { DocumentsApi } from './modules/Documents';
import { FilesApi } from './modules/Files';
import { IapPollsApi } from './modules/IapPolls';
import { InvitationsApi } from './modules/Invitations';
import { LegalDocumentsApi } from './modules/LegalDocuments';
import { LiveStreamsApi } from './modules/LiveStreams/LiveStreamsApi';
import { OnboardingApi } from './modules/Onboarding';
import { OrganizationsApi } from './modules/Organizations';
import { PicturesApi } from './modules/Pictures';
import { PollsApi } from './modules/Polls';
import { ProvidersApi } from './modules/Providers';
import { UserBadgesApi } from './modules/UserBadges';
import { UserContentApi } from './modules/UserContent';
import { UsersApi } from './modules/Users';
import { VideosApi } from './modules/Videos';
import { WPLeaderboardApi } from './modules/WPLeaderboard';
import { WPPlaylistsApi } from './modules/WPPlaylists';

const CACHE_KEY = 'api';

export type IApiRequestOptions = {
  appId?: string;
  skipAuth?: boolean;
  region?: IAppRegion;
};

export type IApiClientAuthorization = {
  sessionId: string;
  region: IAppRegion;
  authToken: string;
  user: IApiGetUserResponseBody;
  isImpersonated?: boolean;
};

export type IApiRequest = Omit<AxiosRequestConfig, 'url'> & {
  path: string;
};

export type IApiJsonRequest<TBody extends object = object> = Omit<
  IApiRequest,
  'responseType' | 'data'
> & {
  expectedKey?: string;
  body?: TBody;
};

export type IApiMockHandlerOptions = {
  loading?: boolean;
};

export type IApiErrorResponseBody = {
  code: string;
  type: string;
  details?: unknown;
  message?: string;
  timestamp: string;
};

export type IApiResponseBody<TData extends object> =
  | {
      status: 'success';
      data: TData;
    }
  | {
      status: 'error';
      errors: Array<IApiErrorResponseBody>;
    };

export class ApiClient {
  public readonly queryClient: QueryClient;
  public readonly appAdmins: AppAdminsApi;
  public readonly appResources: AppResourcesApi;
  public readonly apps: AppsApi;
  public readonly appSettings: AppSettingsApi;
  public readonly articles: ArticlesApi;
  public readonly auth: AuthApi;
  public readonly categories: CategoriesApi;
  public readonly documents: DocumentsApi;
  public readonly files: FilesApi;
  public readonly organizations: OrganizationsApi;
  public readonly pictures: PicturesApi;
  public readonly userBadges: UserBadgesApi;
  public readonly users: UsersApi;
  public readonly videos: VideosApi;
  public readonly builds: BuildsApi;
  public readonly invitations: InvitationsApi;
  public readonly wpPlaylists: WPPlaylistsApi;
  public readonly providers: ProvidersApi;
  public readonly legalDocuments: LegalDocumentsApi;
  public readonly crowd: CrowdApi;
  public readonly liveStreams: LiveStreamsApi;
  public readonly ads: AdsApi;
  public readonly polls: PollsApi;
  public readonly userContent: UserContentApi;
  public readonly iapPolls: IapPollsApi;
  public readonly wpLeaderboard: WPLeaderboardApi;
  public readonly onboarding: OnboardingApi;

  private readonly cache: IStorage;
  private authorization?: IApiClientAuthorization;

  public constructor(public readonly clientId: string) {
    this.cache = storageFactory('local')({ namespace: CACHE_KEY });
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
    });

    // Load modules
    this.appAdmins = new AppAdminsApi(this);
    this.appResources = new AppResourcesApi(this);
    this.apps = new AppsApi(this);
    this.appSettings = new AppSettingsApi(this);
    this.articles = new ArticlesApi(this);
    this.auth = new AuthApi(this);
    this.categories = new CategoriesApi(this);
    this.documents = new DocumentsApi(this);
    this.files = new FilesApi(this);
    this.organizations = new OrganizationsApi(this);
    this.pictures = new PicturesApi(this);
    this.userBadges = new UserBadgesApi(this);
    this.users = new UsersApi(this);
    this.videos = new VideosApi(this);
    this.builds = new BuildsApi(this);
    this.invitations = new InvitationsApi(this);
    this.wpPlaylists = new WPPlaylistsApi(this);
    this.providers = new ProvidersApi(this);
    this.legalDocuments = new LegalDocumentsApi(this);
    this.crowd = new CrowdApi(this);
    this.liveStreams = new LiveStreamsApi(this);
    this.ads = new AdsApi(this);
    this.polls = new PollsApi(this);
    this.userContent = new UserContentApi(this);
    this.iapPolls = new IapPollsApi(this);
    this.wpLeaderboard = new WPLeaderboardApi(this);
    this.onboarding = new OnboardingApi(this);
  }

  public setAuthorization(authorization: IApiClientAuthorization): void {
    this.authorization = authorization;
  }

  public getAuthorization(): IApiClientAuthorization | undefined {
    return this.authorization;
  }

  public getLegacyDashboardUrlWithAuth(
    props: ILegacyDashboardUrlProps,
  ): string | undefined {
    if (!this.authorization) {
      return undefined;
    }

    const url = getLegacyDashboardUrl({
      ...props,
      authToken: this.authorization.authToken,
      userId: this.authorization.user._id,
    });

    return url;
  }

  public clearAuthorization(): void {
    this.cache.clear?.();
    this.authorization = undefined;
  }

  public getCurrentUserId(): string | undefined {
    return this.authorization?.user._id;
  }

  private getAppAuthHeaders(appId: string): Record<string, string> {
    return { 'x-api-key': `appId:${appId}` };
  }

  private requireAppRegionSettings(options?: {
    region?: IAppRegion;
  }): IAppRegionSettings {
    const region =
      options?.region ??
      this.authorization?.region ??
      getAppSettings().defaultRegion;

    const regionSettings = appRegionsSettings[region];
    if (!regionSettings) {
      throw new Error(`Unsupported region \`${region}\`.`);
    }

    return regionSettings;
  }

  private getDashboardAuthHeaders(options?: {
    region?: IAppRegion;
  }): Record<string, string> {
    const { rootAppId } = this.requireAppRegionSettings(options);

    return {
      'x-api-key': rootAppId,
    };
  }

  private getUserAuthHeaders(): Record<string, string> | undefined {
    return {
      authorization: `Bearer ${this.authorization?.authToken ?? ''}`,
    };
  }

  private prepareApiRequest(
    apiRequest: IApiRequest,
    options?: IApiRequestOptions,
  ): AxiosRequestConfig {
    const appAuthHeaders = options?.appId
      ? this.getAppAuthHeaders(options.appId)
      : this.getDashboardAuthHeaders({
          region: options?.region,
        });

    const { apiUrl } = this.requireAppRegionSettings({
      region: options?.region,
    });
    const url = `${apiUrl}${apiRequest.path}`;

    return {
      ...apiRequest,
      headers: {
        ...appAuthHeaders,
        ...(options?.skipAuth ? undefined : this.getUserAuthHeaders()),
        ...apiRequest.headers,
      },
      url,
      paramsSerializer: {
        indexes: null,
      },
    };
  }

  private prepareJsonApiRequest(
    apiJsonRequest: IApiJsonRequest,
    options?: IApiRequestOptions,
  ): IJsonRequest {
    return {
      ...apiJsonRequest,
      ...this.prepareApiRequest(apiJsonRequest, options),
    };
  }

  public async fetch(
    apiRequest: IApiRequest,
    options?: IApiRequestOptions,
  ): Promise<AxiosResponse> {
    const request = this.prepareApiRequest(apiRequest, options);

    return axios.request(request);
  }

  public async fetchJsonStandardized<TResponseBody extends object>(
    request: IApiRequest,
    options?: IApiRequestOptions,
  ): Promise<TResponseBody> {
    const response = await this.fetchJson<IApiResponseBody<TResponseBody>>(
      request,
      options,
    );

    if (response.body.status === 'error') {
      throw new ApiError(response.body.errors, response.context);
    }

    return response.body.data;
  }

  /**
   * @deprecated Use `fetchJsonStandardized` once API response body is
   * standardized.
   */
  public async fetchJson<TResponseBody = unknown>(
    apiJsonRequest: IApiJsonRequest,
    options?: IApiRequestOptions,
  ): Promise<IResponse<TResponseBody>> {
    const jsonRequest = {
      ...this.prepareJsonApiRequest(apiJsonRequest, options),
      validateStatus: () => true,
    };

    return fetchJson(jsonRequest);
  }
}
